import { budgetValue, MONTH_NAMES, type BudgetMap } from '../../audit/budgets';
import type { KpiColumn } from '../../audit/kpis';
import type { BudgetSave, StoreBudgetRow } from '../../data/storeBudgets';
import type { Store } from '../../data/types';
import type { CsvCell } from '../csv';

/**
 * Budgets & goals — the import / export file (B8 2026-10-25; layout per the
 * owner's demo ruling 2026-10-27): ONE file per audit form and year, ONE
 * line per store per month — Store number, Store, Month, then one column per
 * budget / goal question of the form. The same file round-trips: download,
 * fill in Excel, import. Pure.
 */

/** The fixed columns; the form's questions follow, one column each. */
export const BUDGET_FILE_FIXED = ['Store number', 'Store', 'Month'] as const;

/** Twelve months, null = no budget. */
export type Months = (number | null)[];

export const emptyMonths = (): Months => Array.from({ length: 12 }, () => null);

/** What the grid shows: a quarter (phones and most screens) or the whole year (wide screens). */
export type MonthView = 0 | 1 | 2 | 3 | 'all';
export const QUARTER_NAMES = ['Q1', 'Q2', 'Q3', 'Q4'] as const;
export const monthsIn = (view: MonthView): number[] => (view === 'all' ? Array.from({ length: 12 }, (_, m) => m) : [0, 1, 2].map((k) => view * 3 + k));
/** The quarter to open on: this year → the current quarter (Eastern), another year → Q1. */
export const startView = (year: number, today: string): MonthView => (Number(today.slice(0, 4)) === year ? (Math.floor((Number(today.slice(5, 7)) - 1) / 3) as 0 | 1 | 2 | 3) : 0);

/** One store's 12 months for a KPI (copy — safe to edit). */
export function monthsOf(budgets: BudgetMap | undefined, templateKey: string, kpiId: string): Months {
  const row = budgets && Object.prototype.hasOwnProperty.call(budgets, templateKey) ? budgets[templateKey] : undefined;
  const months = row && Object.prototype.hasOwnProperty.call(row, kpiId) ? row[kpiId] : undefined;
  return months && months.length === 12 ? months.map((m) => budgetValue(m)) : emptyMonths();
}

/**
 * The store-year's budgets with ONE KPI's months replaced — every other KPI
 * and template is kept (never drop what this screen doesn't show).
 */
export function withMonths(budgets: BudgetMap | undefined, templateKey: string, kpiId: string, months: Months): BudgetMap {
  const out: BudgetMap = {};
  for (const [key, kpis] of Object.entries(budgets ?? {})) {
    Object.defineProperty(out, key, { value: { ...kpis }, enumerable: true, writable: true, configurable: true });
  }
  if (!Object.prototype.hasOwnProperty.call(out, templateKey)) {
    Object.defineProperty(out, templateKey, { value: {}, enumerable: true, writable: true, configurable: true });
  }
  Object.defineProperty(out[templateKey]!, kpiId, { value: [...months], enumerable: true, writable: true, configurable: true });
  return out;
}

/**
 * A typed or imported budget: blank = no budget (null); thousands
 * separators, "%", "$" and spaces are ignored; anything else that isn't a
 * number (or is beyond ±1e9) is 'bad'.
 */
export function parseBudgetText(text: string): number | null | 'bad' {
  const t = text.trim().replace(/[,%$\s ]/g, '');
  if (t === '') return null;
  if (!/^[-+]?(\d+\.?\d*|\.\d+)$/.test(t)) return 'bad';
  const v = budgetValue(Number(t));
  return v === null ? 'bad' : v;
}

const norm = (s: string) => s.trim().toLowerCase();
/** Headings that are the file's own columns, never a question. */
const FIXED = new Set(['store number', 'store', 'month', 'district', 'year']);

/**
 * The question columns' headings: the question's label — or "label [id]"
 * when two budget questions of the form share a label (or one is named like
 * a fixed column, e.g. "Month"), so each column still names exactly one
 * question.
 */
export function budgetColumnHeadings(columns: readonly KpiColumn[]): string[] {
  const count = new Map<string, number>();
  for (const c of columns) count.set(norm(c.label), (count.get(norm(c.label)) ?? 0) + 1);
  const tag = (c: KpiColumn) => (count.get(norm(c.label)) ?? 0) > 1 || !c.label.trim() || FIXED.has(norm(c.label));
  return columns.map((c) => (tag(c) ? `${c.label.trim()} [${c.id}]`.trim() : c.label.trim()));
}

export function budgetFileHeader(columns: readonly KpiColumn[]): string[] {
  return [...BUDGET_FILE_FIXED, ...budgetColumnHeadings(columns)];
}

/** "Jan 2026" — what the Month column holds. */
export const monthLabel = (month: number, year: number) => `${MONTH_NAMES[month]} ${year}`;

/** Export lines: every store × month of the year (stores in the order given), a value per question. */
export function budgetFileRows(input: {
  stores: readonly Store[];
  columns: readonly KpiColumn[];
  templateKey: string;
  year: number;
  rows: ReadonlyMap<number, StoreBudgetRow>;
}): CsvCell[][] {
  const out: CsvCell[][] = [];
  for (const s of input.stores) {
    const budgets = input.rows.get(Number(s.id))?.budgets;
    const months = input.columns.map((c) => monthsOf(budgets, input.templateKey, c.id));
    for (let m = 0; m < 12; m++) out.push([s.storeNumber, s.name, monthLabel(m, input.year), ...months.map((v) => v[m])]);
  }
  return out;
}

const MONTH_WORDS = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];

function monthWord(w: string): number | null {
  const t = w.toLowerCase().replace(/\.$/, '');
  if (t.length < 3) return null;
  const i = MONTH_WORDS.findIndex((name) => name.startsWith(t) && (t.length === 3 || t === name || (t === 'sept' && name === 'september')));
  return i < 0 ? null : i;
}

const fullYear = (y: string) => (y.length === 2 ? 2000 + Number(y) : Number(y));

/**
 * The Month cell as people (and Excel) write it: "Jan 2026", "January 2026",
 * "Jan-26" (Excel's own re-save of a month), "1-Jan-26", "2026-01",
 * "2026-01-01", "1/2026", or just "Jan" (= the year chosen on the page).
 * A full a/b/yyyy date is read only when it is the 1st of a month — Excel's
 * own date for a month — so a US (8/1/2026) and a UK (01/08/2026) re-save
 * both mean August; any other day is refused rather than guessed (QA
 * 2026-10-27: a day-first date must never land in the wrong month).
 * Month 0-11; null = not a month.
 */
export function parseBudgetMonth(text: string, defaultYear: number): { month: number; year: number } | null {
  const t = text.trim();
  let m: RegExpExecArray | null;
  if ((m = /^([A-Za-z]+\.?)(?:[\s\-/]+('?\d{2}|\d{4}))?$/.exec(t))) {
    const month = monthWord(m[1]!);
    return month === null ? null : { month, year: m[2] ? fullYear(m[2].replace("'", '')) : defaultYear };
  }
  if ((m = /^(\d{4})[-/.](\d{1,2})(?:[-/.]\d{1,2})?(?:[T\s].*)?$/.exec(t))) return valid(Number(m[2]) - 1, Number(m[1]));
  if ((m = /^(\d{1,2})\/(\d{4}|\d{2})$/.exec(t))) return valid(Number(m[1]) - 1, fullYear(m[2]!));
  if ((m = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4}|\d{2})(?:\s.*)?$/.exec(t))) {
    const [a, b] = [Number(m[1]), Number(m[2])];
    if (a !== 1 && b !== 1) return null;
    return valid((b === 1 ? a : b) - 1, fullYear(m[3]!));
  }
  if ((m = /^\d{1,2}[\s-]([A-Za-z]+\.?)[\s-]('?\d{2}|\d{4})$/.exec(t))) {
    const month = monthWord(m[1]!);
    return month === null ? null : valid(month, fullYear(m[2]!.replace("'", '')));
  }
  return null;
}

const valid = (month: number, year: number) => (month >= 0 && month <= 11 && year >= 2000 && year <= 2100 ? { month, year } : null);

export interface ImportSkip {
  /** The spreadsheet row (the header is row 1). */
  line: number;
  reason: string;
}

export interface ImportPlan {
  saves: BudgetSave[];
  /** Stores whose budgets change. */
  stores: number;
  /** Values (store × month × question) that change. */
  values: number;
  skipped: ImportSkip[];
  /** Columns that aren't a budget / goal question of this form — left alone. */
  ignored: string[];
  /** The file can't be read at all (wrong header) — nothing else applies. */
  error?: string;
}


/**
 * A heading → the question it names: a label only one question has (a
 * label may itself end in "[...]" — QA 2026-10-27), "label [id]", or the id.
 */
function questionFor(heading: string, columns: readonly KpiColumn[]): KpiColumn | null {
  const byLabel = columns.filter((c) => norm(c.label) === norm(heading));
  if (byLabel.length === 1) return byLabel[0]!;
  const tagged = /\[([^\]]+)\]\s*$/.exec(heading);
  if (tagged) return columns.find((c) => c.id === tagged[1]!.trim()) ?? null;
  return columns.find((c) => c.id === heading.trim()) ?? null;
}

/**
 * Match an imported file to stores, months and questions and work out what
 * changes. A line = one store's month: Store number + Month find it (the
 * Store name is ignored). A blank cell clears that month's value; a question
 * with no column, and a store-month with no line, are left as they are.
 * Skipped, with why: an unknown store (or one outside your access), a store
 * you can't edit, an unreadable month, another year, a value that isn't a
 * number, the same store + month twice.
 */
export function planBudgetImport(input: {
  table: readonly (readonly string[])[];
  stores: readonly Store[];
  canEdit: (store: Store) => boolean;
  columns: readonly KpiColumn[];
  templateKey: string;
  year: number;
  rows: ReadonlyMap<number, StoreBudgetRow>;
}): ImportPlan {
  const plan: ImportPlan = { saves: [], stores: 0, values: 0, skipped: [], ignored: [] };
  const [header, ...lines] = input.table;
  if (!header || header.every((h) => !h.trim())) return { ...plan, error: 'The file is empty.' };
  const col = (name: string) => header.findIndex((h) => norm(h) === norm(name));
  const storeCol = col('Store number');
  const monthCol = col('Month');
  if (storeCol < 0 || monthCol < 0) {
    const semicolons = header.length === 1 && String(header[0] ?? '').includes(';');
    return {
      ...plan,
      error: semicolons
        ? 'This file uses semicolons between columns (Excel set to a European region). Save it as “CSV UTF-8 (Comma delimited)” and import again.'
        : col('KPI id') >= 0
          ? 'This is the old budgets layout (a line per question). Download the file again — it now has a line per store per month — and copy your numbers into it.'
          : 'This doesn’t look like a budgets file — it needs the columns Store number and Month, then one column per KPI (download one to see the layout).',
    };
  }
  const questionCols: { idx: number; column: KpiColumn }[] = [];
  const taken = new Set<string>();
  header.forEach((h, idx) => {
    if (!h.trim()) return;
    if (FIXED.has(norm(h))) {
      // A second "Month" (say, a question named like the file's own column): say so rather than drop it silently.
      if (idx !== header.findIndex((x) => norm(x) === norm(h))) plan.ignored.push(`${h.trim()} (a second column with this name)`);
      return;
    }
    const q = questionFor(h, input.columns);
    if (!q || taken.has(q.id)) return void plan.ignored.push(h.trim());
    taken.add(q.id);
    questionCols.push({ idx, column: q });
  });
  if (questionCols.length === 0) {
    return { ...plan, error: 'None of the columns is a budget / goal question of this form — check you picked the right form above.' };
  }

  const byNumber = new Map(input.stores.map((s) => [norm(s.storeNumber), s]));
  const seen = new Set<string>();
  const next = new Map<number, BudgetMap | undefined>();
  const changedStores = new Set<number>();

  lines.forEach((cells, i) => {
    const line = i + 2;
    if (cells.every((c) => !c.trim())) return; // a blank line
    const cell = (idx: number) => cells[idx] ?? '';
    const number = cell(storeCol).trim();
    const store = byNumber.get(norm(number));
    if (!store) return void plan.skipped.push({ line, reason: `Store ${number || '(blank)'} isn’t one of your active stores.` });
    if (!input.canEdit(store)) return void plan.skipped.push({ line, reason: `You can’t change store ${store.storeNumber}’s budgets or goals.` });
    const when = parseBudgetMonth(cell(monthCol), input.year);
    if (!when) return void plan.skipped.push({ line, reason: `“${cell(monthCol).trim() || '(blank)'}” isn’t a month (write it like Jan ${input.year}).` });
    if (when.year !== input.year) return void plan.skipped.push({ line, reason: `It’s for ${when.year} — choose that year above to import it.` });
    const key = `${store.id}\n${when.month}`;
    if (seen.has(key)) return void plan.skipped.push({ line, reason: `Store ${store.storeNumber} / ${monthLabel(when.month, when.year)} appears twice — the first usable line was used.` });
    const values: { column: KpiColumn; value: number | null }[] = [];
    for (const { idx, column } of questionCols) {
      const v = parseBudgetText(cell(idx));
      if (v === 'bad') return void plan.skipped.push({ line, reason: `“${cell(idx).trim()}” (${column.label}) isn’t a number.` });
      values.push({ column, value: v });
    }
    // Only a line that was actually used blocks a later duplicate (QA 2026-10-25).
    seen.add(key);
    const storeId = Number(store.id);
    let current = next.has(storeId) ? next.get(storeId) : input.rows.get(storeId)?.budgets;
    for (const { column, value } of values) {
      const months = monthsOf(current, input.templateKey, column.id);
      if (months[when.month] === value) continue;
      months[when.month] = value;
      current = withMonths(current, input.templateKey, column.id, months);
      plan.values += 1;
      changedStores.add(storeId);
    }
    next.set(storeId, current);
  });

  for (const storeId of changedStores) {
    const store = input.stores.find((s) => Number(s.id) === storeId)!;
    const existing = input.rows.get(storeId);
    plan.saves.push({
      storeId,
      storeNumber: store.storeNumber,
      year: input.year,
      id: existing?.id ?? null,
      ...(existing?.eTag ? { eTag: existing.eTag } : {}),
      budgets: next.get(storeId) ?? {},
    });
  }
  plan.stores = changedStores.size;
  return plan;
}
